(() => {
  const COLOUR = 'epg-colour';

  class OwnerFeatures {
    constructor(owner) {
      this.owner = owner;
      this.failed = false;
      this.lastInput = 'pointer';
      this.onKey = () => { this.lastInput = 'keyboard'; };
      this.onPointer = () => { this.lastInput = 'pointer'; };
      document.addEventListener('keydown', this.onKey, { passive: true });
      document.addEventListener('pointerdown', this.onPointer, { passive: true });
      document.addEventListener('wheel', this.onPointer, { passive: true });
    }

    active(params = new URLSearchParams(location.search)) {
      if (this.owner.dataset.colourSort !== 'true' || this.failed) return false;
      return params.get('epg_sort') === 'colour' || (!params.has('sort_by') && params.get('epg_sort') !== 'off');
    }

    normalizeSearchParams(searchParams, event) {
      if (this.owner.dataset.colourSort !== 'true') return searchParams;
      const params = new URLSearchParams(searchParams);
      const select = event?.target?.closest?.('select[name="sort_by"]');
      if (select) params.set('sort_by', select.value);
      if (params.get('sort_by') === COLOUR || (!select && this.active())) {
        this.failed = false;
        params.delete('sort_by');
        params.set('epg_sort', 'colour');
      } else {
        params.delete('epg_sort');
      }
      return params.toString();
    }

    hydrate(grid) {
      if (this.owner.dataset.colourSort !== 'true') return;
      const active = this.active();
      this.owner.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        if (!select.querySelector(`option[value="${COLOUR}"]`)) {
          const option = document.createElement('option');
          option.value = COLOUR;
          option.textContent = this.owner.dataset.colourLabel;
          select.prepend(option);
        }
        select.value = active ? COLOUR : (new URLSearchParams(location.search).get('sort_by') || grid.dataset.nativeSort);
      });
      grid.closest('#ProductGridContainer')?.querySelectorAll('.pagination a').forEach((link) => {
        const url = new URL(link.href, location.href);
        if (active) {
          url.searchParams.delete('sort_by');
          url.searchParams.set('epg_sort', 'colour');
        } else {
          url.searchParams.delete('epg_sort');
          url.searchParams.set('sort_by', new URLSearchParams(location.search).get('sort_by') || grid.dataset.nativeSort);
        }
        link.href = url.href;
      });
    }

    unavailable(grid) {
      this.failed = true;
      const url = new URL(location.href);
      url.searchParams.delete('epg_sort');
      url.searchParams.set('sort_by', grid.dataset.nativeSort || this.owner.dataset.nativeSort);
      history.replaceState({ ...history.state, searchParams: url.searchParams.toString() }, '', url.pathname + url.search + url.hash);
      this.hydrate(grid);
    }

    destroy() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('pointerdown', this.onPointer);
      document.removeEventListener('wheel', this.onPointer);
    }
  }

  class GridFeatures {
    constructor(component) {
      this.component = component;
      this.owner = component.owner;
      this.grid = component.grid;
      this.controls = this.owner.features;
      if (new URLSearchParams(location.search).get('epg_sort') === 'colour' && this.controls) this.controls.failed = false;
      this.abort = new AbortController();
      this.status = component.querySelector('[data-epg-colour-status]');
      this.controls?.hydrate(component);
      this.run();
    }

    current() {
      return !this.abort.signal.aborted && this.component.isConnected && this.owner.querySelector('editorial-product-grid') === this.component;
    }

    async allProducts() {
      const pageCount = Number(this.component.dataset.pages);
      const currentPage = Number(this.component.dataset.page);
      const expected = Number(this.component.dataset.total);
      const pageSize = Number(this.component.dataset.pageSize);
      if (!Number.isInteger(pageCount) || pageCount < 1 || !Number.isInteger(currentPage) || currentPage < 1 || currentPage > pageCount || !Number.isInteger(pageSize) || pageSize < 1 || !Number.isInteger(expected) || expected > 1000) throw new Error('Collection exceeds colour ordering limit');
      const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
      const loaded = await window.PolewardImageColour.mapLimit(pages, 3, async (page) => {
        if (page === currentPage) return this.component.products;
        const url = new URL(location.href);
        url.hash = '';
        url.searchParams.delete('epg_sort');
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.grid.dataset.id);
        url.searchParams.set(this.component.dataset.pageParam || 'page', page);
        const response = await fetch(url, { signal: this.abort.signal });
        if (!response.ok) throw new Error('Could not load collection page');
        const template = document.createElement('template');
        template.innerHTML = await response.text();
        const grid = template.content.querySelector('editorial-product-grid');
        if (!grid || Number(grid.dataset.page) !== page || Number(grid.dataset.total) !== expected || Number(grid.dataset.pageSize) !== pageSize) throw new Error('Collection changed while loading');
        return [...grid.querySelectorAll('.epg-grid > [data-epg-product]')];
      }, this.abort.signal);
      const products = loaded.flat();
      const ids = products.map((product) => product.dataset.productId);
      if (products.length !== expected || ids.some((id) => !id) || new Set(ids).size !== expected) throw new Error('Collection page counts do not match');
      return products;
    }

    setBusy(busy) {
      this.component.toggleAttribute('data-colour-loading', busy);
      this.grid.setAttribute('aria-busy', String(busy));
      this.grid.inert = busy;
      if (this.status) {
        this.status.hidden = !busy;
        this.status.textContent = busy ? this.owner.dataset.colourLoadingLabel : '';
      }
    }

    async run() {
      if (this.controls?.active() && this.component.products.length) {
        this.setBusy(true);
        this.deadline = setTimeout(() => this.abort.abort('timeout'), 20000);
        try {
          if (!window.PolewardImageColour) throw new Error('Colour helper unavailable');
          const products = await this.allProducts();
          const palettes = await window.PolewardImageColour.mapLimit(products, 6,
            (product) => window.PolewardImageColour.imagePalette(product.dataset.colourImage, this.abort.signal), this.abort.signal);
          if (!this.current()) {
            if (this.abort.signal.reason === 'timeout') throw new Error('Colour ordering timed out');
            return;
          }
          const result = window.PolewardImageColour.orderedPage(products.map((node, i) => ({ node, palette: palettes[i], available: node.dataset.productAvailable !== 'false' })),
            Number(this.component.dataset.page), Number(this.component.dataset.pageSize));
          if (result.page.length !== this.component.products.length) throw new Error('Collection page size changed');
          this.component.products = result.page.map(({ node, palette }, i) => {
            node.dataset.epgProduct = i + 1;
            node.dataset.colourTone = palette?.hex || '';
            node.dataset.colourGroup = palette?.group ?? 'unknown';
            node.querySelectorAll('.epg-card__image:not(.epg-card__image--secondary)').forEach((image) => { image.loading = i < 2 ? 'eager' : 'lazy'; });
            return node;
          });
          this.grid.replaceChildren(...this.component.products, ...this.component.inserts);
          this.component.lastLayout = '';
          this.component.layout();
          this.component.dataset.colourState = 'sorted';
          this.component.dataset.colourTotal = products.length;
        } catch (error) {
          if (!this.component.isConnected || (this.abort.signal.aborted && this.abort.signal.reason !== 'timeout')) return;
          this.component.dataset.colourState = 'unavailable';
          this.controls.unavailable(this.component);
          this.failed = true;
        } finally {
          clearTimeout(this.deadline);
          if (this.component.isConnected) {
            this.setBusy(false);
            if (this.failed && this.status) {
              this.status.textContent = this.owner.dataset.colourErrorLabel;
              this.status.hidden = false;
            }
          }
        }
      } else {
        this.component.dataset.colourState = 'native';
      }
      if (this.current()) this.controls?.hydrate(this.component);
      if (this.component.isConnected) this.reveal();
    }

    reveal() {
      const products = this.component.products;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.showAll = () => {
        this.observer?.disconnect();
        products.forEach((product) => { product.dataset.epgReveal = 'shown'; product.style.removeProperty('--epg-reveal-delay'); });
      };
      if (this.owner.dataset.scrollReveal !== 'true' || this.motion.matches || !('IntersectionObserver' in window)) { this.showAll(); return; }
      this.onMotion = () => { if (this.motion.matches) this.showAll(); };
      this.motion.addEventListener('change', this.onMotion);
      products.forEach((product) => { product.dataset.epgReveal = 'pending'; });
      this.observer = new IntersectionObserver((entries) => {
        const columns = Number.parseInt(getComputedStyle(this.owner).getPropertyValue('--epg-columns'), 10) || 2;
        entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
          const index = products.indexOf(entry.target);
          const keyboard = this.controls?.lastInput === 'keyboard';
          entry.target.dataset.epgReveal = keyboard ? 'shown' : 'revealed';
          entry.target.style.setProperty('--epg-reveal-delay', `${keyboard ? 0 : Math.min(index % columns, 5) * 45}ms`);
          this.observer.unobserve(entry.target);
        });
      }, { threshold: 0.05, rootMargin: '0px 0px -16px 0px' });
      products.forEach((product) => this.observer.observe(product));
      this.onFocus = (event) => {
        const card = event.target.closest('[data-epg-product]');
        if (card) { card.dataset.epgReveal = 'shown'; this.observer.unobserve(card); }
      };
      this.grid.addEventListener('focusin', this.onFocus);
    }

    destroy() {
      this.abort.abort();
      clearTimeout(this.deadline);
      this.observer?.disconnect();
      if (this.onMotion) this.motion?.removeEventListener('change', this.onMotion);
      if (this.onFocus) this.grid.removeEventListener('focusin', this.onFocus);
      this.grid.inert = false;
    }
  }

  window.PolewardEditorialFeatures = {
    connectOwner: (owner) => new OwnerFeatures(owner),
    connectGrid: (component) => new GridFeatures(component),
  };
})();
